import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { z } from 'zod';

import { STORAGE_KEYS } from './storageKeys';

import { CustomQuizDetailDefinitionDto } from '@/_generated/api-clients/xp';

// Zod schema for answer validation - matches CustomQuizAnswerDefinitionDTO
// Use loose to allow any extra fields from the API
const QuizAnswerSchema = z
  .object({
    answer: z.record(z.string(), z.string()).optional(),
    correct: z.boolean().optional(),
  })
  .loose();

// Zod schema for question validation - matches CustomQuizQuestionDefinitionDTO
const QuizQuestionSchema = z
  .object({
    question: z.record(z.string(), z.string()).optional(),
    answers: z.array(QuizAnswerSchema).optional(),
  })
  .loose();

// Zod schema for quiz draft validation
// This is a loose validation - we just need to ensure basic structure exists
// Use loose to preserve any API fields we don't explicitly define
const QuizDraftSchema = z
  .object({
    // Quiz content - matches CustomQuizDetailDefinitionDTO structure
    operatorId: z.string(),
    name: z.record(z.string(), z.string()).optional(),
    passingGrade: z.union([z.number(), z.string()]).optional(), // Can be string from form input
    questions: z.array(QuizQuestionSchema).optional(),
    formId: z.string().optional(),
    quizId: z.string().optional(),

    // Metadata for restoration after Okta redirect
    existingQuizId: z.string().nullable().optional(),
    savedAt: z.number(),

    // Flag to indicate a save was in progress when redirected
    pendingSave: z.boolean().optional(),
  })
  .loose(); // Allow any extra fields from the API DTO

// QuizDraft extends the API DTO with metadata fields for draft restoration
export type QuizDraft = CustomQuizDetailDefinitionDto & {
  existingQuizId?: string | null;
  savedAt: number;
  pendingSave?: boolean;
};

/**
 * Parse and validate a quiz draft from unknown data.
 * Returns the validated draft or null if invalid.
 */
const parseQuizDraft = (data: unknown): QuizDraft | null => {
  const result = QuizDraftSchema.safeParse(data);
  if (result.success) {
    return result.data as QuizDraft;
  }
  console.warn('Invalid quiz draft data:', result.error.flatten());
  return null;
};

/**
 * Get the current pendingSave flag directly from localStorage.
 * This avoids race conditions with Jotai's async state updates.
 */
export const getPendingSaveFlagSync = (): boolean => {
  const raw = localStorage.getItem(STORAGE_KEYS.QUIZ_DRAFT);
  if (!raw) return false;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && 'pendingSave' in parsed) {
      return Boolean((parsed as { pendingSave?: boolean }).pendingSave);
    }
    return false;
  } catch (error) {
    console.warn(
      'Failed to parse quiz draft from localStorage when reading pendingSave flag:',
      STORAGE_KEYS.QUIZ_DRAFT,
      error,
    );
    return false;
  }
};

// Custom storage with Zod validation
const quizDraftStorage = createJSONStorage<QuizDraft | null>(
  () => localStorage,
);

const validatedStorage: typeof quizDraftStorage = {
  ...quizDraftStorage,
  getItem: (key: string, initialValue: QuizDraft | null) => {
    const raw = localStorage.getItem(key);
    if (!raw) return initialValue;

    try {
      const parsed: unknown = JSON.parse(raw);

      // Handle explicit null (draft was cleared)
      if (parsed === null) {
        return initialValue;
      }

      const result = QuizDraftSchema.safeParse(parsed);

      if (result.success) {
        return result.data as QuizDraft;
      }

      // Validation failed but DON'T remove the data
      // The data might still be usable, just not matching our strict schema

      // Return the parsed data anyway if it has the minimum required fields
      const maybeValid = parsed as Record<string, unknown>;
      if (
        typeof maybeValid === 'object' &&
        'operatorId' in maybeValid &&
        'savedAt' in maybeValid
      ) {
        return parsed as QuizDraft;
      }

      return initialValue;
    } catch (e: unknown) {
      // JSON parse failed - this is actually corrupted
      localStorage.removeItem(key);
      console.warn('Corrupted quiz draft data removed:', e);
      return initialValue;
    }
  },
};

export const quizDraftAtom = atomWithStorage<QuizDraft | null>(
  STORAGE_KEYS.QUIZ_DRAFT,
  null,
  validatedStorage,
  { getOnInit: true },
);

/**
 * Synchronously set the pendingSave flag on the quiz draft in localStorage.
 * This is needed because Jotai's async state updates may not complete before
 * an Okta redirect happens during a save operation.
 */
export const setPendingSaveFlagSync = (pending: boolean): void => {
  const raw = localStorage.getItem(STORAGE_KEYS.QUIZ_DRAFT);
  if (!raw) return;

  try {
    const parsed: unknown = JSON.parse(raw);
    const draft = parseQuizDraft(parsed);

    if (draft) {
      draft.pendingSave = pending;
      localStorage.setItem(STORAGE_KEYS.QUIZ_DRAFT, JSON.stringify(draft));
    }
  } catch {
    // Ignore parse errors - draft may be invalid
  }
};
